import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Crown, Loader2, UserMinus, X } from "lucide-react";

/** v13 · Panel admin: ver y gestionar el equipo de una cuenta madre Business. */
const inputCls = "w-full rounded-lg border border-[var(--ca-line)] px-3 py-2 text-sm";
const inputStyle = { background: "var(--ca-surface)", color: "var(--ca-text)" };

export default function AdminTeamModal({ owner, onClose, onExtraSeats }) {
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [extra, setExtra] = useState(String(owner.extra_seats || 0));
  const [busy, setBusy] = useState("");
  const [changed, setChanged] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/teams/${owner.id}`).then((r) => setTeam(r.data)).catch((e) => { toast.error(apiError(e)); setTeam({ members: [], invites: [], seats: {} }); });
  }, [owner.id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy("add");
    try {
      const r = await api.post(`/admin/teams/${owner.id}/members`, { email: email.trim() });
      setTeam(r.data); setEmail(""); setChanged(true);
      toast.success("Persona añadida al equipo.");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const remove = async (m) => {
    setBusy(m.id);
    try {
      await api.post(`/admin/teams/${owner.id}/members/${m.id}/remove`);
      toast.success(`${m.email} ya no está en el equipo.`); setChanged(true); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const saveExtra = async () => {
    const n = Math.max(0, parseInt(extra, 10) || 0);
    setBusy("extra");
    const d = await onExtraSeats(n);
    setBusy("");
    if (d) { setChanged(true); load(); }
  };

  const seats = team?.seats || {};
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" data-testid="admin-team-modal" onClick={() => onClose(changed)}>
      <div className="my-8 w-full max-w-xl rounded-xl border border-[var(--ca-line)] p-6" style={{ background: "var(--ca-surface)" }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[var(--ca-text)]">Equipo de {owner.email}</h3>
          <button onClick={() => onClose(changed)} className="text-[var(--ca-subtle)] hover:text-[var(--ca-text)]" aria-label="Cerrar"><X size={18} /></button>
        </div>
        {!team ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={20} style={{ color: "var(--ca-gold)" }} /></div>
        ) : (
          <div className="space-y-5 text-sm text-[var(--ca-text)]">
            <p className="text-[var(--ca-muted)]">
              {seats.used} de {seats.total} plazas ({seats.included} incluidas + {seats.extra} extra) · {seats.pending || 0} invitación(es) pendiente(s) ·
              bolsa {team.searches?.used ?? 0}/{team.searches?.limit ?? "—"} búsquedas este mes
            </p>

            <div className="flex items-end gap-2">
              <label className="flex-1 text-xs font-semibold text-[var(--ca-subtle)]">Usuarios extra contratados
                <input className={`${inputCls} mt-1`} style={inputStyle} inputMode="numeric" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="admin-team-extra" />
              </label>
              <button onClick={saveExtra} disabled={busy === "extra"} className="ca-btn-gold ca-btn-sm disabled:opacity-60">
                {busy === "extra" && <Loader2 size={14} className="animate-spin" />} Guardar
              </button>
            </div>

            <ul className="divide-y divide-[var(--ca-line)] rounded-lg border border-[var(--ca-line)]">
              <li className="flex items-center justify-between px-3 py-2">
                <span className="flex items-center gap-2"><Crown size={13} style={{ color: "var(--ca-gold)" }} /> {team.owner?.email}</span>
                <span className="text-xs" style={{ color: "var(--ca-gold)" }}>Cuenta madre</span>
              </li>
              {(team.members || []).map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="truncate">{m.email}</span>
                  <button onClick={() => remove(m)} disabled={busy === m.id} className="inline-flex items-center gap-1 rounded-md border border-[var(--ca-line)] px-2 py-1 text-xs text-[var(--ca-muted)] hover:text-[var(--ca-text)]">
                    <UserMinus size={12} /> Quitar
                  </button>
                </li>
              ))}
              {(team.invites || []).map((inv) => (
                <li key={inv.invite_id} className="px-3 py-2 text-xs text-[var(--ca-subtle)]">{inv.email} · invitación pendiente</li>
              ))}
            </ul>

            <form onSubmit={add} className="flex items-end gap-2">
              <label className="flex-1 text-xs font-semibold text-[var(--ca-subtle)]">Añadir una cuenta ya registrada
                <input type="email" className={`${inputCls} mt-1`} style={inputStyle} placeholder="correo@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-team-add-email" />
              </label>
              <button type="submit" disabled={busy === "add"} className="ca-btn-gold ca-btn-sm disabled:opacity-60" data-testid="admin-team-add">
                {busy === "add" && <Loader2 size={14} className="animate-spin" />} Añadir
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
